import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";

const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
export const sttSupported = !!SR;

// Voice input via the browser's Web Speech API. Streams the transcript into the
// composer (editable) so the user can review/edit before sending through the
// normal AI pipeline. No backend/Gemini call. Handles permission denial.
export function MicButton({ onTranscript, disabled }) {
  const { language } = useLanguage();
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);

  useEffect(() => () => { try { recRef.current?.stop(); } catch (e) {} }, []);

  const toggle = () => {
    if (!SR) { toast.error("Voice input isn't supported in this browser."); return; }
    if (listening) { try { recRef.current?.stop(); } catch (e) {} setListening(false); return; }
    const rec = new SR();
    rec.lang = language === "hi" ? "hi-IN" : "en-US";
    rec.interimResults = true;
    rec.continuous = false;
    rec.onresult = (e) => {
      let txt = "";
      for (let i = 0; i < e.results.length; i++) txt += e.results[i][0].transcript;
      onTranscript?.(txt);
    };
    rec.onerror = (e) => {
      setListening(false);
      if (e.error === "not-allowed" || e.error === "service-not-allowed")
        toast.error("Microphone permission denied. Please allow mic access in your browser settings.");
      else if (e.error === "no-speech")
        toast.message("Didn't catch that — please try again.");
    };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    try { rec.start(); setListening(true); } catch (e) { setListening(false); }
  };

  if (!SR) return null;

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled}
      data-testid="mic-button"
      aria-label={listening ? "Stop listening" : "Voice input"}
      title={listening ? "Listening… tap to stop" : "Voice input"}
      className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition-colors disabled:opacity-40 ${
        listening ? "animate-pulse bg-rose-500 text-white" : "text-slate-500 hover:bg-slate-100"
      }`}
    >
      {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
    </button>
  );
}
